import { WorkCase } from '@/data/types';

const sphairaConnect: WorkCase = {
  id: "sphaira-connect",
  slug: "sphaira-connect",
  title: "Sphaira Connect: Switch-Installer für macOS",
  description: "Native macOS-App, die Titel per USB auf die Homebrew-App Sphaira der Nintendo Switch überträgt und unpatchte Konsolen per RCM-Payload in Custom-Firmware bringt. Ersetzt ein Python-Skript durch echtes macOS mit Warteschlange, Fortschritt und Liquid-Glass-UI.",
  tags: ["Swift", "SwiftUI", "macOS", "USB", "libusb", "Swift Concurrency", "Reverse Engineering", "Open Source"],
  image: "/assets/img/work/sphaira-connect.webp",
  articleType: "APP",
  date: "2026-07",
  category: "app",
  platforms: ["macOS"],
  applicationCategory: "UtilitiesApplication",
  links: [
    { label: "Download auf GBAtemp", url: "https://gbatemp.net/download/sphaira-connect.39970/" },
    { label: "Quellcode auf GitHub", url: "https://github.com/Marvbuster/sphaira-connect" }
  ],
  detailContent: {
    subtitle: "Ein reverse-engineertes USB-Protokoll, stundenlange Blocking-Transfers und ein Hardware-Exploit, sauber verpackt in eine native App mit Warteschlange, Live-Fortschritt und Liquid-Glass-Oberfläche",
    challenge: [
      {
        text: "Die Switch-Homebrew-App Sphaira installiert Titel per USB, doch mit ihrem 1.0-Release hat sie ihr altes, Tinfoil-kompatibles Protokoll gegen ein eigenes ausgetauscht. Über Nacht funktionierten die etablierten Tools nicht mehr, und übrig blieb ein mitgeliefertes Python-Skript als einziger unterstützter Weg. Ein Skript, das eine Python-Umgebung braucht, keine Warteschlange kennt und beim Fortschritt schweigt."
      },
      {
        text: "Als Mac-Nutzer und Bastler wollte ich das ordentlich: eine echte macOS-App, die mehrere Titel als Queue annimmt, Geschwindigkeit und Restzeit anzeigt, ein Log führt, und die man einfach öffnet, statt ein Terminal zu bemühen. Kein Zwischenschritt, kein Python."
      },
      {
        text: "Zwei Dinge machten das anspruchsvoll:"
      },
      {
        listing: [
          "Das neue USB-Protokoll ist nirgends dokumentiert, es musste aus dem Python-Skript und dem Verhalten der Konsole reverse-engineert werden",
          "USB-Bulk-Transfers blockieren, bei mehreren GB großen Titeln über Minuten bis Stunden, ohne die reaktive Oberfläche einer nativen App auszubremsen oder abbrechbar zu bleiben"
        ]
      }
    ],
    solution: [
      {
        text: "Entstanden ist eine native SwiftUI-App mit vier strikt getrennten Schichten, deren Abhängigkeiten nur in eine Richtung zeigen. Diese Trennung ist kein Stilmittel, sondern in einem Contract festgeschrieben, gegen den jedes Modul einzeln gebaut wurde. Vier Aspekte trugen das Projekt."
      },
      {
        subheadline: "Das Protokoll neu aufgebaut",
        text: "Sphaira und die App tauschen nichts als 24-Byte-Pakete aus: sechs Little-Endian-UInt32-Werte, abgesichert per CRC32C, gefolgt von den rohen Nutzdaten. Die Argument-Felder haben keine feste Bedeutung, sondern werden je nach Richtung und Zustand der State-Machine anders gelesen. Genau dieses Format habe ich aus dem Python-Skript rekonstruiert und als sauber getestete Swift-Implementierung neu gebaut, Paket-Layout, Prüfsumme und Installations-Ablauf inklusive."
      },
      {
        subheadline: "Stundenlange Transfers ohne eingefrorene UI",
        text: "Swifts moderne Nebenläufigkeit stellt per Default alles auf den MainActor, für eine App, deren Kernaufgabe stundenlange blockierende USB-Aufrufe sind, genau die falsche Voreinstellung. Deshalb sind die Protokoll- und USB-Schichten bewusst nonisolated, die Session läuft in einem detached Task, und jede Rückmeldung kommt über exakt einen geordneten AsyncStream zurück in die Oberfläche. Abbrechen läuft nicht über Task-Cancellation, sondern über ein Lock-gesichertes Flag, das die blockierenden Schleifen zwischen zwei USB-Timeouts abfragen."
      },
      {
        subheadline: "Zweiter Modus: Konsole erst öffnen",
        text: "Viele Konsolen sind noch gar nicht für Homebrew freigeschaltet. Dafür hat die App einen zweiten, komplett eigenständigen Modus: manuelle RCM-Payload-Injektion über den Fusée-Gelée-Hardware-Exploit für ältere, unpatchte Geräte. Mit eingebauten Presets für Hekate und Fusée, die ihre offiziellen Releases direkt von GitHub laden und vor jeder Injektion per SHA-256 gegenprüfen. Injiziert wird ausschließlich auf Knopfdruck, nie automatisch. Beide Modi teilen sich nur die UI-Hülle, auf Transport- und Session-Ebene sind es getrennte USB-Geräte, getrennte Modelle, getrennte Schleifen."
      },
      {
        subheadline: "Testbarkeit als Architekturprinzip",
        text: "Die Grenze zwischen der reinen Core-Schicht und der USB-Schicht ist von der Testbarkeit getrieben: Das Test-Target bindet die Core-Logik per Symlink direkt ein, sodass Protokoll, Prüfsummen und Fortschrittsberechnung ohne echte Hardware geprüft werden, mit über sechzig Tests. Die Oberfläche folgt einem Liquid-Glass-Designsystem mit einer klaren Regel: kein Glas auf Glas. Das Fenster wechselt seine Stimmung mit der Phase des Transfers."
      }
    ],
    results: [
      {
        text: "Sphaira Connect ist quelloffen (GPL-3.0), auf GBAtemp veröffentlicht und im Einsatz. Aus einem Terminal-Skript wurde eine App, die man einfach benutzt:"
      },
      {
        listing: [
          "Natives macOS statt Python-Skript: Titel per Drag-and-drop, Warteschlange, Live-Geschwindigkeit, Restzeit und Log",
          "Reverse-engineertes SPH0-USB-Protokoll als getestete Swift-Implementierung neu aufgebaut, mit CRC32C-Absicherung",
          "Stundenlange Blocking-Transfers ohne eingefrorene UI, jederzeit sauber abbrechbar",
          "Zweiter Modus für RCM-Payload-Injektion (Fusée Gelée) mit Hekate-/Fusée-Presets direkt von GitHub",
          "Über sechzig Tests gegen die Core-Logik ohne echte Hardware, zweisprachig (DE/EN)"
        ]
      },
      {
        text: "Das Projekt ist mein liebstes Beispiel dafür, wie Bastel-Neugier und sauberes Engineering zusammenkommen: ein undokumentiertes Protokoll, ein Hardware-Exploit und die harte Realität blockierender Hardware-I/O, am Ende verpackt in eine App, die sich anfühlt wie aus einem Guss."
      }
    ],
    technologies: [
      "Swift",
      "SwiftUI",
      "Swift Concurrency (Actors)",
      "libusb",
      "C-Bridging",
      "CRC32C",
      "AppKit",
      "XCTest"
    ],
    codeExamples: [
      {
        language: "swift",
        description: "Das SPH0-Paket: sechs Little-Endian-UInt32-Werte, abgesichert per CRC32C. Die vier Argument-Felder haben keine feste Bedeutung, deshalb bietet der Typ benannte Sichten (command, dataOffset, dataSize) auf dieselben Rohdaten.",
        code: `// Core/SphairaProtocol.swift — 24-Byte-Wire-Format
struct Packet {
    var magic: UInt32
    var arg2, arg3, arg4, arg5: UInt32   // Bedeutung je nach Richtung/State
    var crc32c: UInt32

    // Benannte Sichten auf dieselben Rohfelder
    var command: UInt32 { arg2 }
    var dataOffset: UInt64 { UInt64(arg3) | (UInt64(arg4) << 32) }
    var dataSize: UInt32 { arg5 }

    func encoded() -> Data {
        var body = Data()
        for value in [magic, arg2, arg3, arg4, arg5] {
            body.append(contentsOf: withUnsafeBytes(of: value.littleEndian, Array.init))
        }
        let checksum = CRC32C.checksum(body)     // über die ersten 20 Bytes
        body.append(contentsOf: withUnsafeBytes(of: checksum.littleEndian, Array.init))
        return body
    }
}`
      },
      {
        language: "swift",
        description: "Abbrechen ohne Task.cancel(): Die blockierenden USB-Schleifen laufen nonisolated in einem detached Task. Cancellation kommt über ein Lock-gesichertes Flag, das zwischen zwei USB-Timeouts gepollt wird, statt über kooperative Task-Cancellation, die ein blockierender libusb-Aufruf nie sähe.",
        code: `// USB/InstallSession.swift — Abbruch zwischen zwei Timeouts
final class CancelFlag: @unchecked Sendable {
    private let lock = NSLock()
    private var cancelled = false

    func cancel() { lock.withLock { cancelled = true } }
    var isCancelled: Bool { lock.withLock { cancelled } }
}

nonisolated func runTransfer(_ item: InstallItem, cancel: CancelFlag) throws {
    while let chunk = try nextChunk(item) {
        if cancel.isCancelled { throw InstallError.cancelled }
        try transport.bulkWrite(chunk, timeout: .milliseconds(500))
        await events.yield(.progress(item.id, chunk.count))  // einziger Rückkanal
    }
}`
      }
    ],
    images: [
      {
        src: "/assets/img/work/sphaira-connect_install.webp",
        alt: "Sphaira Connect Installer mit Titel-Warteschlange und laufendem USB-Transfer",
        type: "image",
        title: "Der Installer-Modus",
        caption: "Warteschlange mehrerer Titel, laufender USB-Transfer mit Geschwindigkeit, Restzeit und Fortschritt pro Datei."
      },
      {
        src: "/assets/img/work/sphaira-connect_rcm.webp",
        alt: "Sphaira Connect RCM-Payload-Injektion mit Hekate- und Fusée-Presets, erfolgreich abgeschlossen",
        type: "image",
        title: "Der RCM-Modus",
        caption: "Manuelle Payload-Injektion mit Hekate- und Fusée-Presets, SHA-256-geprüft. Die Injektion startet nur auf Knopfdruck, nie automatisch."
      }
    ]
  }
};

export default sphairaConnect;
